import {
  DAppConnector,
} from '@hashgraph/hedera-wallet-connect';
import {
  AccountCreateTransaction,
  AccountId,
  ContractExecuteTransaction,
  ContractId,
  Hbar,
  KeyList,
  LedgerId,
  PublicKey,
  ScheduleCreateTransaction,
  ScheduleSignTransaction,
  Signer,
  Timestamp,
  TokenAssociateTransaction,
  TokenId,
  Transaction,
  TransactionId,
  TransactionReceiptQuery,
  TransferTransaction,
  AccountUpdateTransaction,
} from '@hashgraph/sdk';
import { getAccountData } from '../api/userAPI';
import { getSubmittedTransaction } from '../api/mirrorAPI';
import { DEADLINE_MARGIN_MS, executeWithHederaCheck } from '../services/walletExecution';
import { isWalletRejection } from '../utils/walletErrors';
import { createClient } from '../signing/ClientFactory';
import { Buffer } from 'buffer';
import { NetworkType } from '../utils/constants';

interface WalletExtension {
  id: string;
  name: string;
  available: boolean;
}

class WalletConnector {
  private dAppConnector: DAppConnector | null = null;
  public extensionInstalled: boolean = true; // WalletConnect doesn't need extension check

  setAccountId: (accountId: string) => void;
  setConnected: (connected: boolean) => void;
  setHashPackAvailable: ((available: boolean) => void) | null = null;

  constructor(
    setAccountId: (accountId: string) => void,
    setConnected: (connected: boolean) => void,
    setHashPackAvailable?: (available: boolean) => void
  ) {
    this.setAccountId = setAccountId;
    this.setConnected = setConnected;
    this.setHashPackAvailable = setHashPackAvailable || null;
  }

  /**
   * Initialize the WalletConnect connector
   */
  async initHashconnect(network: NetworkType) {
    try {
      const metadata = {
        name: 'Hedera Multisig',
        description: 'Hedera Multisig Wallet Application',
        url: window.location.origin,
        icons: [window.location.origin + '/logo192.png'],
      };

      // Map network to LedgerId
      const ledgerId = this.networkToLedgerId(network);

      // Get WalletConnect Project ID from environment or use a default for testing
      const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '315db8ec6d61e5b1cc864b8eafd14627';

      this.dAppConnector = new DAppConnector(
        metadata,
        ledgerId,
        projectId
      );

      // Initialize with minimal logging to reduce console noise
      await this.dAppConnector.init({ logger: 'fatal' });

      // Set up event listeners
      this.setUpHashConnectEvents();

      // Check for existing sessions
      const sessions = this.dAppConnector.signers;
      if (sessions && sessions.length > 0) {
        const accountId = sessions[0].getAccountId();
        if (accountId) {
          this.setAccountId(accountId.toString());
          this.setConnected(true);
        }
      }

      // Check for HashPack extension availability
      this.checkHashPackExtension();
    } catch (error) {
      console.error('Failed to initialize WalletConnect:', error);
      // Don't throw - allow app to continue
    }
  }

  /**
   * Check if HashPack extension is available
   */
  private checkHashPackExtension() {
    if (!this.dAppConnector) return;

    // Give extensions time to register (they respond via message protocol)
    setTimeout(() => {
      const hashPackAvailable = this.isHashPackExtensionAvailable();
      if (this.setHashPackAvailable) {
        this.setHashPackAvailable(hashPackAvailable);
      }
    }, 500);
  }

  /**
   * Check if HashPack extension is detected and available
   */
  isHashPackExtensionAvailable(): boolean {
    if (!this.dAppConnector) return false;

    const extensions = (this.dAppConnector as any).extensions as WalletExtension[] | undefined;
    if (!extensions || extensions.length === 0) return false;

    const hashPack = extensions.find(
      (ext) => ext.name?.toLowerCase().includes('hashpack') && ext.available
    );
    return !!hashPack;
  }

  /**
   * Get the HashPack extension ID if available
   */
  private getHashPackExtensionId(): string | null {
    if (!this.dAppConnector) return null;

    const extensions = (this.dAppConnector as any).extensions as WalletExtension[] | undefined;
    if (!extensions || extensions.length === 0) return null;

    const hashPack = extensions.find(
      (ext) => ext.name?.toLowerCase().includes('hashpack') && ext.available
    );
    return hashPack?.id || null;
  }

  /**
   * Convert network string to LedgerId
   */
  private networkToLedgerId(network: NetworkType): LedgerId {
    switch (network.toLowerCase()) {
      case 'mainnet':
        return LedgerId.MAINNET;
      case 'testnet':
        return LedgerId.TESTNET;
      case 'previewnet':
        return LedgerId.PREVIEWNET;
      default:
        return LedgerId.TESTNET;
    }
  }

  /**
   * Set up event listeners for wallet connection
   */
  setUpHashConnectEvents() {
    if (!this.dAppConnector) return;

    // Session event handler is set via onSessionIframeCreated callback
    this.dAppConnector.onSessionIframeCreated = (session) => {
      // Session connected
      const signers = this.dAppConnector?.signers;
      if (signers && signers.length > 0) {
        const accountId = signers[0].getAccountId();
        if (accountId) {
          this.setAccountId(accountId.toString());
          this.setConnected(true);
        }
      }
    };
  }

  /**
   * Connect to wallet via WalletConnect modal (shows all available wallets)
   */
  async connectToExtension() {
    return this.connectViaWalletConnect();
  }

  /**
   * Connect via WalletConnect modal - shows all available wallets
   */
  async connectViaWalletConnect() {
    if (!this.dAppConnector) {
      throw new Error('Connector not initialized');
    }

    try {
      await this.dAppConnector.openModal();

      // Wait a bit for connection to establish
      await new Promise(resolve => setTimeout(resolve, 1000));

      const signers = this.dAppConnector.signers;
      if (signers && signers.length > 0) {
        const accountId = signers[0].getAccountId();
        if (accountId) {
          this.setAccountId(accountId.toString());
          this.setConnected(true);
        }
      }
    } catch (error) {
      console.error('Failed to connect via WalletConnect:', error);
      throw error;
    }
  }

  /**
   * Connect directly to HashPack extension (bypasses wallet selection modal)
   */
  async connectToHashPackExtension() {
    if (!this.dAppConnector) {
      throw new Error('Connector not initialized');
    }

    const extensionId = this.getHashPackExtensionId();
    if (!extensionId) {
      throw new Error('HashPack extension not found. Please install HashPack or use WalletConnect.');
    }

    try {
      await (this.dAppConnector as any).connectExtension(extensionId);

      // Wait a bit for connection to establish
      await new Promise(resolve => setTimeout(resolve, 1000));

      const signers = this.dAppConnector.signers;
      if (signers && signers.length > 0) {
        const accountId = signers[0].getAccountId();
        if (accountId) {
          this.setAccountId(accountId.toString());
          this.setConnected(true);
        }
      }
    } catch (error) {
      console.error('Failed to connect to HashPack extension:', error);
      throw error;
    }
  }

  /**
   * Get connected wallet peer metadata (name, icon) from the WalletConnect session
   */
  getConnectedWalletMeta(): { name: string; icon: string | null } | null {
    if (!this.dAppConnector?.walletConnectClient) return null;

    const sessions = this.dAppConnector.walletConnectClient.session.getAll();
    if (!sessions || sessions.length === 0) return null;

    const session = sessions[sessions.length - 1];
    const peer = session.peer?.metadata;
    if (!peer) return null;

    return {
      name: peer.name || 'Wallet',
      icon: peer.icons?.[0] || null,
    };
  }

  /**
   * Has the wallet sign a sign-in text (hedera_signMessage) and returns the
   * base64 SignatureMap the backend checks.
   */
  async signLoginMessage(accountId: string, network: NetworkType, message: string): Promise<string> {
    if (!this.dAppConnector) {
      throw new Error('Connector not initialized');
    }
    const result: any = await this.dAppConnector.signMessage({
      signerAccountId: `hedera:${network}:${accountId}`,
      message,
    });
    // The typings wrap it in a JSON-RPC result; at run time it can come bare
    const signatureMap = result?.signatureMap ?? result?.result?.signatureMap;
    if (!signatureMap) {
      throw new Error('The wallet returned no signature');
    }
    return signatureMap;
  }

  /**
   * Get signer for transactions
   */
  getSigner(network: NetworkType, accountToSign: string): Signer {
    if (!this.dAppConnector) {
      throw new Error('Connector not initialized');
    }

    const signers = this.dAppConnector.signers;
    if (!signers || signers.length === 0) {
      throw new Error('No signers available');
    }

    // Return the first signer (for the connected account)
    return signers[0];
  }

  /**
   * Get connected account ID
   */
  getAccountId(): string | null {
    if (!this.dAppConnector) return null;

    const signers = this.dAppConnector.signers;
    if (!signers || signers.length === 0) return null;

    // This is synchronous in v1.3.7, but returns a promise
    // We'll handle it in the initialization
    return null;
  }

  /**
   * Disconnect wallet
   */
  async clearPairings() {
    if (this.dAppConnector) {
      try {
        await this.dAppConnector.disconnectAll();
        this.setAccountId('');
        this.setConnected(false);
      } catch (error) {
        console.error('Failed to disconnect:', error);
      }
    }
  }

  /**
   * Executes `frozenTx` through the wallet. The wallet's reply can get lost on
   * its way back (see services/walletExecution), so the mirror node is asked
   * for the transaction at the same time.
   *
   * When the wallet replies, its transaction ID and the node receipt are used
   * as before. When only the mirror confirms, its record is used: nodes keep a
   * receipt for just 3 minutes, which a late confirmation can miss. With
   * `receipt: false` no receipt is fetched; a failure is then left to whoever
   * reads the transaction next.
   */
  private async executeThroughWallet(
    frozenTx: Transaction,
    signer: Signer,
    network: NetworkType,
    { receipt = true }: { receipt?: boolean } = {}
  ): Promise<{ transactionId: TransactionId; scheduleId?: string; scheduledTransactionId?: string }> {
    const sentId = frozenTx.transactionId!;
    const deadline =
      sentId.validStart!.toDate().getTime() + frozenTx.transactionValidDuration * 1000 + DEADLINE_MARGIN_MS;

    const outcome = await executeWithHederaCheck(
      () => frozenTx.executeWithSigner(signer),
      () => getSubmittedTransaction(sentId.toString(), network),
      deadline,
      { isRejection: isWalletRejection },
    );

    if (outcome.via === 'hedera') {
      const { result, entity_id } = outcome.record;
      // Worded like a receipt error, so callers matching a status still match
      if (result !== 'SUCCESS') throw new Error(`Transaction ${sentId} failed on Hedera with status ${result}`);
      return {
        transactionId: sentId,
        scheduleId: entity_id ?? undefined,
        scheduledTransactionId: `${sentId}?scheduled`,
      };
    }

    const transactionId = outcome.response.transactionId ?? sentId;
    if (!receipt) return { transactionId };

    // A direct Client, not the DAppSigner: its query path fails with
    // "Query.fromBytes() not implemented for type getByKey"
    const client = createClient(network as 'testnet' | 'mainnet');
    try {
      const nodeReceipt = await new TransactionReceiptQuery().setTransactionId(transactionId).execute(client);
      return {
        transactionId,
        scheduleId: nodeReceipt.scheduleId?.toString(),
        scheduledTransactionId: nodeReceipt.scheduledTransactionId?.toString(),
      };
    } finally {
      client.close();
    }
  }

  /**
   * Create multisig account
   */
  async createMultisigAccount(
    publicKeyList: any[],
    threshold: number,
    accountId: string,
    network: NetworkType,
    memo?: string
  ) {
    const thresholdKey = new KeyList(publicKeyList, threshold);
    const signer = this.getSigner(network, accountId);

    const transaction = await new AccountCreateTransaction()
      .setKey(thresholdKey)
      .setAccountMemo(memo ?? '')
      .setInitialBalance(new Hbar(1))
      .freezeWithSigner(signer);

    // The backend reads the receipt itself, so none is fetched here
    const { transactionId } = await this.executeThroughWallet(transaction, signer, network, { receipt: false });
    return transactionId;
  }

  /**
   * Create scheduled send token transaction
   * @param tx - Transaction info (from, to, amount)
   * @param network - Network to execute on
   * @param signerAccountId - Account ID of the signer
   * @param expirationTime - Optional expiration time for the scheduled transaction
   * @param waitForExpiry - If true, transaction executes only at expiration time
   */
  async createSendTokenScheduledTransaction(
    tx: { from: string; to: string; amount: number },
    network: NetworkType,
    signerAccountId: string,
    expirationTime?: Date,
    waitForExpiry: boolean = false
  ): Promise<{ scheduleId: string; txId: string }> {
    const signer = this.getSigner(network, signerAccountId);

    const transferTransaction = new TransferTransaction()
      .addHbarTransfer(tx.from, new Hbar(-1 * tx.amount))
      .addHbarTransfer(tx.to, new Hbar(tx.amount));

    // Add a unique memo to prevent IDENTICAL_SCHEDULE_ALREADY_CREATED errors
    const uniqueMemo = `multisig-${Date.now()}`;

    let scheduledTx = new ScheduleCreateTransaction()
      .setScheduledTransaction(transferTransaction)
      .setPayerAccountId(AccountId.fromString(tx.from))
      .setScheduleMemo(uniqueMemo);

    // Set expiration time — use provided value or default to 62 days (Hedera max)
    const effectiveExpiration = expirationTime || new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
    scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

    // Set wait for expiry flag
    if (waitForExpiry) {
      scheduledTx = scheduledTx.setWaitForExpiry(true);
    }

    const frozenTx = await scheduledTx.freezeWithSigner(signer);
    const { scheduleId, scheduledTransactionId } = await this.executeThroughWallet(frozenTx, signer, network);

    return {
      scheduleId: scheduleId as string,
      txId: scheduledTransactionId as string,
    };
  }

  /**
   * Sign scheduled send tokens transaction
   */
  async signScheduledTransaction(
    scheduleId: string,
    accountId: string,
    network: NetworkType
  ) {
    const signer = this.getSigner(network, accountId);

    const transaction = await new ScheduleSignTransaction()
      .setScheduleId(scheduleId)
      .freezeWithSigner(signer);

    // Executing through the wallet signs it too; no signWithSigner needed
    const { transactionId } = await this.executeThroughWallet(transaction, signer, network);
    return { transactionId };
  }

  /**
   * Prepare a token association transaction for multi-signature collection.
   * TokenAssociateTransaction cannot be scheduled on Hedera (SCHEDULED_TRANSACTION_NOT_IN_WHITELIST),
   * so we use manual signature collection instead.
   *
   * The transaction is created with a future validStart time to allow time for signature collection.
   * All signatures must be collected before validStart + 180 seconds.
   *
   * @param accountId - The multisig account to associate the token with
   * @param tokenId - The token ID to associate
   * @param network - The Hedera network (testnet/mainnet)
   * @param signerAccountId - The account ID of the first signer
   * @param expirationTime - When the transaction should be executable (validStart)
   * @returns Object with signed transaction bytes, public key, and transaction info
   */
  async prepareTokenAssociateTransaction(
    accountId: string,
    tokenId: string,
    network: NetworkType,
    signerAccountId: string,
    expirationTime?: Date,
  ): Promise<{
    frozenTxBytes: string; // Base64 encoded FROZEN transaction bytes (unsigned base for all signers)
    signedTxBytes: string; // Base64 encoded signed transaction bytes (with this signer's signature)
    publicKey: string; // Hex encoded public key of the signer
    txId: string;
    validStart: number; // Unix timestamp of when transaction becomes valid
  }> {
    if (!this.dAppConnector) {
      throw new Error('Connector not initialized');
    }

    const signer = this.getSigner(network, signerAccountId);

    // Create a client for the network
    const client = createClient(network as 'testnet' | 'mainnet');

    // Use multiple node account IDs for redundancy (standard consensus nodes)
    const nodeAccountIds = [
      new AccountId(3),
      new AccountId(4),
      new AccountId(5),
    ];

    // Create the token association transaction
    const tokenAssociateTx = new TokenAssociateTransaction()
      .setNodeAccountIds(nodeAccountIds)
      .setAccountId(AccountId.fromString(accountId))
      .setTokenIds([TokenId.fromString(tokenId)]);

    // Set transaction ID with the multisig account and specified validStart
    if (expirationTime) {
      const validStartTimestamp = Timestamp.fromDate(expirationTime);
      const transactionId = TransactionId.withValidStart(
        AccountId.fromString(accountId),
        validStartTimestamp
      );
      tokenAssociateTx.setTransactionId(transactionId);
    } else {
      const transactionId = TransactionId.generate(AccountId.fromString(accountId));
      tokenAssociateTx.setTransactionId(transactionId);
    }

    // Freeze the transaction with the client (required before signing)
    const frozenTx = tokenAssociateTx.freezeWith(client);

    // Store the FROZEN transaction bytes as the unsigned base for all signers
    const frozenTxBytes = frozenTx.toBytes();

    // Get the signer's public key from Hedera
    let publicKey = '';
    let pubKeyObj: InstanceType<typeof PublicKey> | null = null;
    try {
      const accountData = await getAccountData(signerAccountId, network);
      try {
        pubKeyObj = PublicKey.fromBytesED25519(Buffer.from(accountData.key, 'hex'));
      } catch (e) {
        try {
          pubKeyObj = PublicKey.fromBytesECDSA(Buffer.from(accountData.key, 'hex'));
        } catch (e2) {
          pubKeyObj = PublicKey.fromString(accountData.key);
        }
      }
      publicKey = pubKeyObj.toStringDer();
    } catch (error) {
      console.error('Failed to get signer public key:', error);
      throw new Error('Failed to get signer public key');
    }

    // Sign the transaction with the wallet
    const signedTx = await signer.signTransaction(frozenTx);

    // Serialize the signed transaction - toBytes() captures both the transaction body
    // and all embedded signatures, following the Hedera-recommended pattern
    const signedTxBytesArr = signedTx.toBytes();

    // Get transaction ID and valid start
    const txId = signedTx.transactionId;
    const validStart = txId?.validStart?.seconds?.toNumber() || Math.floor(Date.now() / 1000);

    return {
      frozenTxBytes: Buffer.from(frozenTxBytes).toString('base64'),
      signedTxBytes: Buffer.from(signedTxBytesArr).toString('base64'),
      publicKey,
      txId: txId?.toString() || '',
      validStart,
    };
  }

  /**
   * Sign an existing token association transaction (for subsequent signers).
   * Receives partially signed transaction bytes, adds this signer's signature,
   * and returns the updated bytes.
   *
   * @param signedTxBytes - Base64 encoded partially signed transaction bytes
   * @param network - The Hedera network (testnet/mainnet)
   * @param signerAccountId - The account ID of this signer
   * @returns Object with updated signed transaction bytes and public key
   */
  async signTokenAssociateTransaction(
    currentSignedTxBytes: string, // Base64 encoded transaction bytes with existing signatures
    network: NetworkType,
    signerAccountId: string,
  ): Promise<{
    signedTxBytes: string; // Base64 encoded updated transaction bytes with new signature
    publicKey: string; // Hex encoded public key of the signer
  }> {
    const signer = this.getSigner(network, signerAccountId);

    // Restore the transaction from bytes (preserves existing signatures)
    const txBytes = Buffer.from(currentSignedTxBytes, 'base64');
    const transaction = Transaction.fromBytes(txBytes);

    // Get the signer's public key from Hedera
    let publicKey = '';
    try {
      const accountData = await getAccountData(signerAccountId, network);
      let pubKeyObj: InstanceType<typeof PublicKey>;
      try {
        pubKeyObj = PublicKey.fromBytesED25519(Buffer.from(accountData.key, 'hex'));
      } catch (e) {
        try {
          pubKeyObj = PublicKey.fromBytesECDSA(Buffer.from(accountData.key, 'hex'));
        } catch (e2) {
          pubKeyObj = PublicKey.fromString(accountData.key);
        }
      }
      publicKey = pubKeyObj.toStringDer();
    } catch (error) {
      console.error('Failed to get signer public key:', error);
      throw new Error('Failed to get signer public key');
    }

    // Sign the transaction - adds this signer's signature to existing ones
    const signedTx = await signer.signTransaction(transaction);

    // Serialize with all accumulated signatures
    const updatedTxBytes = signedTx.toBytes();

    return {
      signedTxBytes: Buffer.from(updatedTxBytes).toString('base64'),
      publicKey,
    };
  }

  /**
   * Create scheduled token transfer transaction
   */
  async createTokenTransferScheduledTransaction(
    tx: { from: string; to: string; amount: number; tokenId: string; decimals: number },
    network: NetworkType,
    signerAccountId: string,
    expirationTime?: Date,
    waitForExpiry: boolean = false
  ): Promise<{ scheduleId: string; txId: string }> {
    const signer = this.getSigner(network, signerAccountId);

    // Use string manipulation to avoid floating-point precision issues with crypto amounts
    const amountStr = tx.amount.toString();
    const [intPart, decPart = ''] = amountStr.split('.');
    const paddedDecimal = decPart.padEnd(tx.decimals, '0').slice(0, tx.decimals);
    const rawBigInt = BigInt(intPart + paddedDecimal);
    if (rawBigInt > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new Error('Amount exceeds safe integer precision');
    }
    const rawAmount = Number(rawBigInt);

    const parsedTokenId = TokenId.fromString(tx.tokenId);

    const transferTransaction = new TransferTransaction()
      .addTokenTransfer(parsedTokenId, tx.from, -rawAmount)
      .addTokenTransfer(parsedTokenId, tx.to, rawAmount);

    const uniqueMemo = `multisig-${Date.now()}`;

    let scheduledTx = new ScheduleCreateTransaction()
      .setScheduledTransaction(transferTransaction)
      .setPayerAccountId(AccountId.fromString(tx.from))
      .setScheduleMemo(uniqueMemo);

    // Set expiration time — use provided value or default to 62 days (Hedera max)
    const effectiveExpiration = expirationTime || new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
    scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

    if (waitForExpiry) {
      scheduledTx = scheduledTx.setWaitForExpiry(true);
    }

    const frozenTx = await scheduledTx.freezeWithSigner(signer);
    const { scheduleId, scheduledTransactionId } = await this.executeThroughWallet(frozenTx, signer, network);

    return {
      scheduleId: scheduleId as string,
      txId: scheduledTransactionId as string,
    };
  }

  /**
   * Placeholder for extension detection (not needed for WalletConnect)
   */
  foundExtension() {
    // WalletConnect doesn't require browser extension
    this.extensionInstalled = true;
  }

  /**
   * Create scheduled contract execution transaction
   * @param contractId - Hedera contract ID (0.0.xxxxx format)
   * @param functionData - ABI-encoded function call data (from ethers.Interface.encodeFunctionData)
   * @param gas - Gas limit for the contract call
   * @param payableAmount - Amount of HBAR to send with the call (for payable functions)
   * @param payerAccountId - Account that will pay for the scheduled transaction
   * @param network - Network to execute on
   * @param signerAccountId - Account ID of the signer
   * @param expirationTime - Optional expiration time for the scheduled transaction
   * @param waitForExpiry - If true, transaction executes only at expiration time
   */
  async createContractExecuteScheduledTransaction(
    contractId: string,
    functionData: Uint8Array,
    gas: number,
    payableAmount: number,
    payerAccountId: string,
    network: NetworkType,
    signerAccountId: string,
    expirationTime?: Date,
    waitForExpiry: boolean = false
  ): Promise<{ scheduleId: string; txId: string }> {
    const signer = this.getSigner(network, signerAccountId);

    // Create the contract execute transaction
    const contractExecuteTx = new ContractExecuteTransaction()
      .setContractId(ContractId.fromString(contractId))
      .setGas(gas)
      .setFunctionParameters(functionData);

    // Add payable amount if specified
    if (payableAmount > 0) {
      contractExecuteTx.setPayableAmount(new Hbar(payableAmount));
    }

    // Add a unique memo to prevent IDENTICAL_SCHEDULE_ALREADY_CREATED errors
    const uniqueMemo = `multisig-${Date.now()}`;

    // Wrap in ScheduleCreateTransaction
    let scheduledTx = new ScheduleCreateTransaction()
      .setScheduledTransaction(contractExecuteTx)
      .setPayerAccountId(AccountId.fromString(payerAccountId))
      .setScheduleMemo(uniqueMemo);

    // Set expiration time — use provided value or default to 62 days (Hedera max)
    const effectiveExpiration = expirationTime || new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
    scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

    // Set wait for expiry flag
    if (waitForExpiry) {
      scheduledTx = scheduledTx.setWaitForExpiry(true);
    }

    const frozenTx = await scheduledTx.freezeWithSigner(signer);
    const { scheduleId, scheduledTransactionId } = await this.executeThroughWallet(frozenTx, signer, network);

    return {
      scheduleId: scheduleId as string,
      txId: scheduledTransactionId as string,
    };
  }

  /**
   * Create scheduled threshold update transaction (AccountUpdateTransaction)
   * @param accountId - The multisig account ID to update
   * @param newPublicKeys - Array of public key strings for the new KeyList
   * @param newThreshold - The new threshold (e.g., 2 for 2-of-3)
   * @param payerAccountId - Account that will pay for the scheduled transaction
   * @param network - Network to execute on
   * @param signerAccountId - Account ID of the signer
   */
  async createThresholdUpdateScheduledTransaction(
    accountId: string,
    newPublicKeys: string[],
    newThreshold: number,
    payerAccountId: string,
    network: NetworkType,
    signerAccountId: string,
  ): Promise<{ scheduleId: string; txId: string }> {
    const signer = this.getSigner(network, signerAccountId);

    // Build KeyList from public keys with threshold
    const publicKeys = newPublicKeys.map(key => PublicKey.fromString(key));
    const thresholdKey = new KeyList(publicKeys, newThreshold);

    // Create AccountUpdateTransaction to update the account's key
    const accountUpdateTx = new AccountUpdateTransaction()
      .setAccountId(AccountId.fromString(accountId))
      .setKey(thresholdKey);

    // Add a unique memo to prevent IDENTICAL_SCHEDULE_ALREADY_CREATED errors
    const uniqueMemo = `multisig-threshold-${Date.now()}`;

    // Wrap in ScheduleCreateTransaction
    let scheduledTx = new ScheduleCreateTransaction()
      .setScheduledTransaction(accountUpdateTx)
      .setPayerAccountId(AccountId.fromString(payerAccountId))
      .setScheduleMemo(uniqueMemo);

    // Set expiration time — default to 62 days (Hedera max), matching other transaction types
    const effectiveExpiration = new Date(Date.now() + 62 * 24 * 60 * 60 * 1000);
    scheduledTx = scheduledTx.setExpirationTime(Timestamp.fromDate(effectiveExpiration));

    // Defaults to waitForExpiry false
    scheduledTx = scheduledTx.setWaitForExpiry(false);

    const frozenTx = await scheduledTx.freezeWithSigner(signer);
    const { scheduleId, scheduledTransactionId } = await this.executeThroughWallet(frozenTx, signer, network);

    return {
      scheduleId: scheduleId as string,
      txId: scheduledTransactionId as string,
    };
  }

  /**
   * Send HTS tokens directly from the connected wallet to another account
   */
  async sendTokenToAccount(
    account_id: string,
    amount: number,
    tokenId: string,
    decimals: number,
    network: NetworkType,
    signerAccountId: string,
  ) {
    const signer = this.getSigner(network, signerAccountId);

    // Convert human-readable amount to raw amount using string manipulation
    const amountStr = amount.toString();
    const [intPart, decPart = ''] = amountStr.split('.');
    const paddedDecimal = decPart.padEnd(decimals, '0').slice(0, decimals);
    const rawBigInt = BigInt(intPart + paddedDecimal);
    if (rawBigInt > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new Error('Amount exceeds safe integer precision');
    }
    const rawAmount = Number(rawBigInt);

    const parsedTokenId = TokenId.fromString(tokenId);

    const transferTx = new TransferTransaction()
      .addTokenTransfer(parsedTokenId, signerAccountId, -rawAmount)
      .addTokenTransfer(parsedTokenId, account_id, rawAmount);

    const frozenTx = await transferTx.freezeWithSigner(signer);
    const { transactionId } = await this.executeThroughWallet(frozenTx, signer, network);

    return {
      txId: transactionId.toString(),
    };
  }

  async sendHbarToAccount(account_id: string, amount: number, network: NetworkType, signerAccountId: string) {
    const signer = this.getSigner(network, signerAccountId);

    const sendHbarTx = new TransferTransaction()
      .addHbarTransfer(signerAccountId, new Hbar(-amount))
      .addHbarTransfer(account_id, new Hbar(amount));

    const frozenTx = await sendHbarTx.freezeWithSigner(signer);
    const { transactionId } = await this.executeThroughWallet(frozenTx, signer, network);

    return {
      txId: transactionId.toString(),
    };
  }
}


export default WalletConnector;
